import type { CSSProperties } from 'react';
import type { ThemeMode } from '@/types/ConfigOrg';

function normalizeHex(color: string): string {
  const c = color.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(c)) return c;
  if (/^#[0-9A-Fa-f]{3}$/.test(c)) {
    return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
  }
  return '#219268';
}

function hexToRgb(hex: string) {
  const h = normalizeHex(hex).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b]
    .map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0'))
    .join('')}`;
}

function mixWithWhite(hex: string, whiteRatio: number) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(
    r + (255 - r) * whiteRatio,
    g + (255 - g) * whiteRatio,
    b + (255 - b) * whiteRatio,
  );
}

function darken(hex: string, amount: number) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

function accentSoftColor(baseHex: string, lightSoftHex: string, isDark: boolean): string {
  if (isDark) {
    const { r, g, b } = hexToRgb(baseHex);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
  }
  return lightSoftHex;
}

export function buildBrandCssVariables(
  brandColor: string,
  fontColor?: string,
  themeMode: ThemeMode = 'light',
  bodyFontColor?: string,
): CSSProperties {
  const primary = normalizeHex(brandColor);
  const isDark = themeMode === 'dark';
  const font = isDark ? '#F1F5F9' : fontColor ? normalizeHex(fontColor) : '#344054';
  // Body/secondary text: use explicitly configured value, fallback to auto-derived
  const body = isDark
    ? '#94A3B8'
    : bodyFontColor
      ? normalizeHex(bodyFontColor)
      : mixWithWhite(font, 0.55);

  // Table cell: 70% opacity of primary font on white bg = mix 30% white into font
  // e.g. fontColor #344054 → table cell color #71798B
  const tableCell = isDark ? '#94A3B8' : mixWithWhite(font, 0.30);
  // Table header: same as primary font color
  const tableHeader = isDark ? '#F1F5F9' : font;

  const { r, g, b } = hexToRgb(primary);

  return {
    '--heading-color': isDark ? '#F1F5F9' : '#0C1B33',
    '--brand-primary': primary,
    '--brand-primary-hover': darken(primary, 0.12),
    '--brand-primary-soft': isDark
      ? `rgba(${r}, ${g}, ${b}, 0.12)`
      : mixWithWhite(primary, 0.9),
    '--brand-primary-muted': isDark
      ? `rgba(${r}, ${g}, ${b}, 0.20)`
      : mixWithWhite(primary, 0.82),
    '--brand-primary-dark': darken(primary, 0.35),
    '--brand-teal-soft': accentSoftColor('#0891B2', '#ECFEFF', isDark),
    '--brand-green-soft': accentSoftColor('#059669', '#ECFDF5', isDark),
    '--brand-amber-soft': accentSoftColor('#D97706', '#FFFBEB', isDark),
    '--brand-purple-soft': accentSoftColor('#7C3AED', '#F5F3FF', isDark),
    '--brand-red-soft': accentSoftColor('#DC2626', '#FEF2F2', isDark),
    '--brand-font-color': font,
    '--brand-font-color-muted': body,
    '--table-cell-color': tableCell,
    '--table-header-color': tableHeader,
    '--surface': isDark ? '#111827' : '#FFFFFF',
    '--surface-muted': isDark ? '#0B0F19' : '#F9FAFB',
    '--surface-subtle': isDark ? '#080B11' : '#F0F2F5',
    '--border-color': isDark ? '#1F2937' : '#E4E7EC',
    '--border-color-light': isDark ? '#111827' : '#F2F4F7',
  } as CSSProperties;
}

const DEFAULT_BRAND_CSS_VARIABLES = buildBrandCssVariables(
  '#219268',
  '#344054',
  'light',
  '#667085',
);

export function applyBrandCssVariablesToRoot(
  brandColor: string,
  fontColor?: string,
  themeMode: ThemeMode = 'light',
  bodyFontColor?: string,
): void {
  const vars = buildBrandCssVariables(brandColor, fontColor, themeMode, bodyFontColor);
  Object.entries(vars).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value as string);
  });
}

export function resetBrandCssVariablesOnRoot(): void {
  Object.entries(DEFAULT_BRAND_CSS_VARIABLES).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value as string);
  });
}

export function applyFaviconToDocument(faviconUrl: string | null | undefined): void {
  const href = faviconUrl || '/favicon.png';
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = href.endsWith('.ico') ? 'image/x-icon' : 'image/png';
  link.href = href;
}
